import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, ListGroup, Alert, Row, Col } from 'react-bootstrap';

const SharePointFolderBrowser = ({ show, onClose, onSelect, encryptionKey }) => {
  const [sites, setSites] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [currentSite, setCurrentSite] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderIdStack, setFolderIdStack] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) {
      setSites([])
      setFolders([])
      setCurrentPath([])
      setCurrentSite(null)
      setCurrentFolder(null)
      setFolderIdStack([])
    }
    if (show && encryptionKey) {
      fetchSites();
    }
  }, [show, encryptionKey]);

  const fetchSites = async () => {
    setLoadingSites(true);
    setError(null);
    try {
      const response = await fetch(`/v0/sharepoint/sites?encryption_key=${encryptionKey}`);
      if (!response.ok) throw new Error('Failed to fetch sites');
      const data = await response.json();
      setSites(data || []);
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError('Failed to fetch SharePoint sites. Please try again.');
    } finally {
      setLoadingSites(false);
    }
  };

  const fetchFolders = async (site, path = [], folderId = '') => {
    setLoadingFolders(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        encryption_key: encryptionKey,
        site_id: site.id,
      });
      
      // Always use folder ID for navigation if available
      if (folderId) {
        params.append('parent_folder_id', folderId);
      } else if (path.length > 0) {
        // Fallback to path-based navigation if no ID is provided (for backward compatibility)
        params.append('parent_folder', path.join('/'));
      }
      
      const response = await fetch(`/v0/sharepoint/folder?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch folders');
      
      const data = await response.json();
      setFolders(data.filter(item => item.folder) || []);
      setCurrentSite(site);
      setCurrentPath(path);
      // Update current folder if we have a folder ID
      if (folderId) {
        setCurrentFolder({ id: folderId, name: path[path.length - 1] || 'Root' });
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError('Failed to fetch folders. Please try again.');
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleFolderClick = (folder) => {
    const newPath = [...currentPath, folder.name];
    // Only fetch folders if the clicked folder is different from the current folder
    if (!currentFolder || folder.id !== currentFolder.id) {
      // Save the current folder to the stack before navigating
      setFolderIdStack(prev => [...prev, currentFolder]);
      setCurrentFolder(folder);
      fetchFolders(currentSite, newPath, folder.id);
    }
  };

  const handleSiteSelect = (site) => {
    setCurrentFolder(null);
    setFolderIdStack([]);
    setCurrentSite(site);
    fetchFolders(site, [], '');
  };

  const handleSelect = () => {
    if (currentSite && currentFolder) {
        onSelect({
            sharepointFolder: {
                site_id: currentSite.id,
                site_name: currentSite.displayName || currentSite.title || 'Unknown Site',
                folder_id: currentFolder.id,
                folder_name: currentFolder.name || 'Unknown Folder',
                file_path: currentPath.join('/')
            }
        });
        onClose();
    }
  };

  const navigateUp = () => {
    if (folderIdStack.length > 0) {
      const newPath = [...currentPath];
      newPath.pop();
      // Get the parent folder from the stack
      const parentFolder = folderIdStack[folderIdStack.length - 1];
      // Remove the last folder from the stack
      setFolderIdStack(prev => prev.slice(0, -1));
      // Update current folder to the parent
      setCurrentFolder(parentFolder);
      // Navigate using the parent folder ID
      fetchFolders(currentSite, newPath, parentFolder?.id || '');
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-folder-open me-2" /> Select SharePoint Folder
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Row className="mb-3">
          <Col>
              <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4>Sites</h4>
              </div>
            {loadingSites ? (
              <div className="text-center my-3">
                <Spinner animation="border" />
              </div>
            ) : (
              <ListGroup>
                {sites.map((site) => (
                  <ListGroup.Item
                    key={site.id}
                    action
                    active={currentSite?.id === site.id}
                    onClick={() => handleSiteSelect(site)}
                  >
                    {site.displayName || site.name || 'Unnamed Site'}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Col>
          <Col className="d-flex flex-column">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4>Folders</h4>
              {currentPath.length > 0 && (
                <Button variant="link" onClick={navigateUp} size="sm">
                  <i className="fa fa-arrow-up" /> Up
                </Button>
              )}
            </div>
            {loadingFolders ? (
              <div className="text-center my-3">
                <Spinner animation="border" />
              </div>
            ) : currentSite ? (
              <ListGroup>
                {folders.map((folder, index) => (
                  <ListGroup.Item
                    key={index}
                    action
                    onClick={() => handleFolderClick(folder)}
                  >
                    <i className="fa fa-folder me-2" />
                    {folder.name}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              <Alert variant="warning">Select a site to view folders</Alert>
            )}
            {currentPath.length > 0 && (
              <div className="mt-auto pt-2">
                  <h4>Selected Path</h4>
                  <ListGroup.Item active className="text-white">/{currentPath.join('/')}</ListGroup.Item>
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSelect}
          disabled={!currentFolder}
        >
          Select
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SharePointFolderBrowser;
